import { useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api-client';
import {
  createFolder,
  deleteFolderNode,
  finalizeUploads,
  getArchiveImports,
  importArchive,
  presignUploads,
  putToS3WithProgress,
  setFolderNodeDomain,
  uploadDocumentsBatch,
  type ArchiveImportStatus,
  type BulkUploadMeta,
  type VaultBulkUploadResult,
} from './api';

/**
 * The vault's background upload manager.
 *
 * Uploads used to live inside the upload dialog's component state, so the dialog had to stay
 * open — and the person had to wait — until the last file landed. This module owns the upload
 * loop instead: a dialog hands a selection over with `startBatch` and closes; the batch keeps
 * running while the person moves around the app, and `UploadTray` renders progress from this
 * store wherever they are. The transfer itself is still browser → S3 (the bytes are on the
 * person's machine), so the tab has to stay open — the tray warns on unload while running.
 *
 * A plain module store read through `useSyncExternalStore`: no provider, no new dependency.
 */

export type ItemStatus = 'queued' | 'uploading' | 'importing' | 'finalizing' | 'done' | 'failed';

export interface UploadItem {
  file: File;
  status: ItemStatus;
  pct: number;
  error?: string;
  documentNumber?: string;
  /** The reservation this row's bytes went to, kept so a retry can ask whether it landed rather
   *  than uploading again (see runFiles). Set once the bytes are in storage. */
  uploadId?: number;
  /** For an archive item: the unzip summary once it finishes. */
  note?: string;
  /** A "done" that is not a plain success: nothing was stored, because the file is already in
   *  that folder. Drawn as a caution, not a green check. */
  outcome?: 'skipped';
  /** For an archive item: the files that were skipped, and why (capped list from the server). */
  skipped?: { name: string; reason: string }[];
  /** True total skipped (may exceed the detail list length). */
  skippedCount?: number;
  /** For an archive item: the files whose earlier copy this import RETIRED (capped list). */
  replaced?: string[];
  /** True total replaced (may exceed the detail list length). */
  replacedCount?: number;
}

/** Where a batch's archives unzip to (regular files are placed by `meta` alone). */
export interface UploadDestination {
  /** The current folder node — archives unzip UNDER it. */
  folderId?: number | null;
  /** The folder the person typed or the view locked; created on demand when there is no node. */
  folderName?: string;
  /** Set when the typed folder should be created against a product. */
  productId?: number | null;
}

export interface UploadBatch {
  id: string;
  /** Where the files went, for the tray's heading ("Uploading to Clinical / 3 of 12"). */
  label: string;
  items: UploadItem[];
  running: boolean;
  /** Anything a person should look at before this disappears: a failure, a skipped file, or an
   *  archive that RETIRED an earlier copy. Clean batches can be hidden without review. */
  needsReview: boolean;
  meta: BulkUploadMeta;
  destination: UploadDestination;
  onSettled?: () => void;
}

interface Snapshot {
  batches: UploadBatch[];
}

// Each batch uploads in rounds of UPLOAD_CHUNK presigns.
// How long to keep asking whether an archive has finished: 1,500 × 1.2 s = 30 minutes.
// How long the tray keeps watching before it says "still unpacking" and stops asking. Wall
// clock now, not a poll count, because the interval grows — 30 minutes either way. A big
// archive on a busy server outlived the old 12 minutes and the row wrongly said it failed.
export const ARCHIVE_WATCH_MS = 30 * 60_000;
const UPLOAD_CHUNK = 25;
// A 429 on a round is the server PACING a large folder (~2,000 files a minute per user), not a
// failure of those files: wait, then go again. The window is a sliding minute, so a short retry
// interval picks the round back up soon after budget frees; the ceiling bounds a server that
// stays busy for reasons other than us.
export const BUSY_RETRY_MS = 10_000;
// Zips go out a few at a time, not all at once. The import worker takes ONE archive at a time
// fleet-wide (the admission doorman), so firing eighty at the server buys nothing and only
// guarantees a pile of 429s. A small number keeps the tray moving without the stampede.
export const ARCHIVE_SEND_AT_ONCE = 3;
// How often the tray asks about the batch. It starts quick, because a small zip really might be
// finished, then backs off: an archive queued behind fifty others does not need asking every
// second, and every one of those answers is "still waiting".
const ARCHIVE_POLL_MIN_MS = 1_200;
const ARCHIVE_POLL_MAX_MS = 5_000;
const ARCHIVE_POLL_GROWTH = 1.6;
// Matches the route's own cap on how many ids one call may carry.
export const ARCHIVE_POLL_CHUNK = 500;
export const BUSY_MAX_WAIT_MS = 5 * 60_000;
const BUSY_NOTE = 'Waiting — the server is pacing this upload; it resumes on its own.';

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const t = setTimeout(done, ms);
    function done() { signal.removeEventListener('abort', done); clearTimeout(t); resolve(); }
    signal.addEventListener('abort', done);
  });

/** Run one round's server call, waiting out 429s. `onWait` marks the rows so the person sees
 *  why nothing is moving; the ceiling turns a 429 that never clears into the server's own reason. */
async function whileServerBusy<T>(call: () => Promise<T>, signal: AbortSignal, onWait: () => void): Promise<T> {
  let waited = 0;
  for (;;) {
    try {
      return await call();
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 429 || waited >= BUSY_MAX_WAIT_MS || signal.aborted) throw e;
      onWait();
      await sleep(BUSY_RETRY_MS, signal);
      waited += BUSY_RETRY_MS;
    }
  }
}
// Zip/tar-family archives are UNZIPPED (their contents imported, folder structure preserved).
const ARCHIVE_EXT = ['.zip', '.tar', '.tgz', '.gz', '.bz2', '.xz'];

export function isArchiveFile(name: string): boolean {
  const lower = name.toLowerCase();
  return ARCHIVE_EXT.some((ext) => lower.endsWith(ext));
}

let snapshot: Snapshot = { batches: [] };
const listeners = new Set<() => void>();
// Aborted when the session ends. Every transfer in flight listens to it, so signing out stops
// the bytes rather than only hiding the tray — see cancelAllUploads.
//
// Read it ONCE per run and pass that signal down. cancelAllUploads replaces this controller
// straight after aborting it, so a guard that re-reads this variable later is reading the
// NEXT session's controller and can never fire.
let sessionCancel = new AbortController();
let seq = 0;

function emit() {
  listeners.forEach((l) => l());
}

function setBatches(update: (batches: UploadBatch[]) => UploadBatch[]) {
  snapshot = { batches: update(snapshot.batches) };
  emit();
}

function patchBatch(id: string, patch: Partial<UploadBatch> | ((b: UploadBatch) => Partial<UploadBatch>)) {
  setBatches((bs) => bs.map((b) => (b.id === id ? { ...b, ...(typeof patch === 'function' ? patch(b) : patch) } : b)));
}

// Position-based status updates so duplicate filenames never collide.
function patchItem(id: string, idx: number, patch: Partial<UploadItem>) {
  patchBatch(id, (b) => ({ items: b.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
}

function batch(id: string): UploadBatch | undefined {
  return snapshot.batches.find((b) => b.id === id);
}

export function subscribeUploads(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getUploadSnapshot(): Snapshot {
  return snapshot;
}

export function isUploading(): boolean {
  return snapshot.batches.some((b) => b.running);
}

/** How many files have not landed yet — the number an exit warning should name. Counts the
 *  unfinished rows of running batches, not the batches, because "2 uploads still going" is what
 *  a person can act on. */
export function uploadsInFlight(): number {
  return snapshot.batches
    .filter((b) => b.running)
    .reduce((n, b) => n + b.items.filter(
      (it) => it.status !== 'done' && it.status !== 'failed').length, 0);
}

/** Read the manager from a component. Re-renders on every progress tick. */
export function useUploadBatches(): UploadBatch[] {
  return useSyncExternalStore(subscribeUploads, () => snapshot.batches, () => snapshot.batches);
}

const JUNK_NOTE = 'System file — not a document, not stored.';
// No "recoverable" claim: the vault has no restore endpoint, so the retired copy is gone from
// every view even though its stored file is kept (audit 2026-09-22).
const skipNote = (reason: string | null | undefined) => (reason ? `Skipped — ${reason}.` : JUNK_NOTE);

function applyResult(id: string, idx: number, r: VaultBulkUploadResult) {
  if (r.status === 'skipped') {
    // A folder upload's file the server did not store: OS junk (no reason), or a file already in
    // that folder (the server's reason). Rendered the same way on both upload paths.
    patchItem(id, idx, { status: 'done', pct: 100, note: skipNote(r.note), outcome: 'skipped' });
    return;
  }
  patchItem(id, idx, {
    status: r.status === 'ok' ? 'done' : 'failed',
    pct: 100,
    error: r.error ?? undefined,
    documentNumber: r.document_number ?? undefined,
  });
}

// Regular files → the normal upload rounds (direct-S3 presign, or through-app fallback). Each
// entry carries its ORIGINAL item index so statuses land on the right row when archives are
// interleaved in the selection.
async function runFiles(
  id: string, idxsIn: number[], meta: BulkUploadMeta, signal: AbortSignal,
): Promise<number> {
  let idxs = idxsIn;
  let okCount = 0;
  // Rows whose bytes already reached storage: their failure was the CONFIRMATION, so asking
  // again is the whole retry. Doing this first means a lost reply never mints a second document.
  const resumable = idxs.filter((i) => batch(id)?.items[i]?.uploadId != null);
  if (resumable.length > 0) {
    const ids = resumable.map((i) => batch(id)!.items[i]!.uploadId!);
    try {
      resumable.forEach((i) => patchItem(id, i, { status: 'finalizing', pct: 100 }));
      const res = await finalizeUploads(ids, signal);
      resumable.forEach((i, k) => { const r = res.results[k]; if (r) applyResult(id, i, r); });
      okCount += res.ok_count;
      idxs = idxs.filter((i) => !resumable.includes(i));
    } catch {
      // The reservation is gone or its bytes never arrived — forget it and upload afresh.
      resumable.forEach((i) => patchItem(id, i, { uploadId: undefined }));
    }
  }

  for (let base = 0; base < idxs.length; base += UPLOAD_CHUNK) {
    const round = idxs.slice(base, base + UPLOAD_CHUNK);
    const current = batch(id);
    if (!current) return okCount;
    const chunk = round.map((i) => current.items[i]?.file).filter((f): f is File => f != null);
    const idxAt = (i: number) => round[i] ?? 0;
    const markWaiting = () => round.forEach((i) => patchItem(id, i, { status: 'queued', note: BUSY_NOTE }));
    const clearWaiting = () => round.forEach((i) => patchItem(id, i, { note: undefined }));
    let presign: Awaited<ReturnType<typeof presignUploads>>;
    try {
      // Files, not names: each carries its folder-relative path (a folder pick) so the server files
      // it in the right subfolder — see presignUploads.
      presign = await whileServerBusy(() => presignUploads(chunk, meta), signal, markWaiting);
    } catch (e) {
      if (signal.aborted) return okCount;
      const message = e instanceof Error ? e.message : 'Upload failed.';
      round.forEach((i) => patchItem(id, i, { status: 'failed', error: message, note: undefined }));
      continue;
    }
    clearWaiting();
    if (signal.aborted) return okCount;
    if (presign.fallback) {
      round.forEach((i) => patchItem(id, i, { status: 'uploading', pct: 100 }));
      const res = await whileServerBusy(() => uploadDocumentsBatch(chunk, meta, signal), signal, markWaiting);
      res.results.forEach((r, i) => applyResult(id, idxAt(i), r));
      okCount += res.ok_count;
    } else {
      // The server reserved nothing for these positions (OS junk, or a file already in that
      // folder — with its reason). Mark them and drop them from the pairing below, so every later
      // file still meets ITS reservation.
      const skipped = new Set(presign.skipped_indices ?? []);
      const reasons = new Map((presign.skipped ?? []).map((s) => [s.index, s.reason]));
      skipped.forEach((i) => patchItem(id, idxAt(i), {
        status: 'done', pct: 100, note: skipNote(reasons.get(i)), outcome: 'skipped',
      }));
      const kept = chunk.map((file, i) => ({ file, i })).filter(({ i }) => !skipped.has(i));
      const finalizable: { idx: number; uploadId: number }[] = [];
      await Promise.all(presign.uploads.map((u, k) => (async () => {
        const pair = kept[k];
        if (!pair) return;
        const file = pair.file;
        const idx = idxAt(pair.i);
        patchItem(id, idx, { status: 'uploading', pct: 0 });
        try {
          // Only when the whole percent moves: the row displays `pct` rounded, so a finer tick
          // redraws every row of the batch to show the identical number.
          let shown = -1;
          await putToS3WithProgress(u.presigned, file, (pct) => {
            if (pct === shown) return;
            shown = pct;
            patchItem(id, idx, { pct });
          }, signal);
          patchItem(id, idx, { status: 'finalizing', pct: 100, uploadId: u.upload_id });
          finalizable.push({ idx, uploadId: u.upload_id });
        } catch (e) {
          patchItem(id, idx, { status: 'failed', error: e instanceof Error ? e.message : 'Upload failed.' });
        }
      })()));
      // Nothing is confirmed after the session ends — a file that finished moving would
      // otherwise still be filed, under whoever the browser is authenticated as by then.
      if (signal.aborted) return okCount;
      if (finalizable.length > 0) {
        const ordered = finalizable.sort((a, b) => a.idx - b.idx);
        const res = await finalizeUploads(ordered.map((o) => o.uploadId), signal);
        ordered.forEach((o, k) => { const r = res.results[k]; if (r) applyResult(id, o.idx, r); });
        okCount += res.ok_count;
      }
    }
  }
  return okCount;
}

// Archives → the unzip pipeline: upload to /archives (streams to a temp file), then poll the
// background job until it lands. Each unzips UNDER the destination folder or at the root.
async function runArchives(id: string, idxs: number[], signal: AbortSignal): Promise<number> {
  if (idxs.length === 0) return 0;
  const current = batch(id);
  if (!current) return 0;
  const { destination, meta } = current;
  // Destination for the archive(s): the current folder node, else the typed folder (created
  // if needed), else the vault root. Each archive's tree is unzipped UNDER it (wrapper stripped),
  // so a named upload lands as ONE main folder with the sections inside — not scattered roots.
  let parentId: number | undefined = destination.folderId ?? undefined;
  // Remembered separately from parentId: only a folder THIS run created may be taken back if the
  // import never happens. An existing destination is not ours to remove.
  let createdFolderId: number | undefined;
  if (parentId == null) {
    const typed = (destination.folderName ?? '').trim();
    if (typed && typed !== 'Document') {
      try {
        // create-only on the server: a name that already exists is a 409, NOT the existing
        // folder handed back. That is deliberate — merging into someone else's folder let this
        // run treat it as its own and delete it on failure (2026-09-18 audit). So a clash fails
        // the archive with the server's reason rather than silently landing it at the root.
        const f = await createFolder(typed, destination.productId ?? undefined);
        parentId = f.id ?? undefined;
        createdFolderId = f.id ?? undefined;
        // Pin the destination's domain — a parent with no direct files of its own would
        // otherwise derive 'other' and land in the wrong rail section.
        if (parentId != null && meta.domain && meta.domain !== 'other') {
          try { await setFolderNodeDomain(parentId, meta.domain); } catch { /* non-fatal */ }
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Could not create the folder.';
        idxs.forEach((idx) => patchItem(id, idx, { status: 'failed', error: msg }));
        return 0;
      }
    }
  }
  let okCount = 0;
  // Did any archive actually LAND? Not "did the server accept it" — an archive upload is answered
  // the moment it is queued, and the problem (too many files, a bad entry) is only found later
  // when the worker opens it. Treating acceptance as success kept the destination folder for an
  // import that was then refused, leaving an empty folder as a tidy-looking trace of something
  // that never happened. The OUTCOME decides, and the watcher below already has it.
  // Sending and watching are separate jobs with different shapes. Sending is per-file, paced,
  // and patient about a busy server. WATCHING is per-BATCH: one loop asks about every archive
  // at once, because a loop per file cost one request per file per second and the worker only
  // imports one archive at a time anyway.
  const watching = new Map<number, number>();     // job id -> tray row
  let sending = true;

  const report = (idx: number, st: ArchiveImportStatus) => {
    if (st.status === 'done') {
      okCount += 1;
      const skip = st.skipped_count ? `, ${st.skipped_count} skipped` : '';
      // Replacements are named, not just counted: each one RETIRED the copy that was in the
      // folder, so "3 replaced" without saying which documents is not something you can check.
      const repl = st.replaced_count ? `, ${st.replaced_count} replaced` : '';
      patchItem(id, idx, {
        status: 'done', pct: 100,
        note: `${st.files_created} file${st.files_created === 1 ? '' : 's'} into ${st.folders_touched} folder${st.folders_touched === 1 ? '' : 's'}${repl}${skip}`,
        skipped: st.skipped ?? [],
        skippedCount: st.skipped_count,
        replaced: st.replaced ?? [],
        replacedCount: st.replaced_count,
      });
    } else {
      patchItem(id, idx, { status: 'failed', error: st.error ?? 'Unzip failed.' });
    }
  };

  const send = async () => {
    // A few at a time. The fleet imports one archive at a time, so a stampede only produces 429s.
    for (let i = 0; i < idxs.length; i += ARCHIVE_SEND_AT_ONCE) {
      if (signal.aborted) break;
      await Promise.all(idxs.slice(i, i + ARCHIVE_SEND_AT_ONCE).map(async (idx) => {
        const file = batch(id)?.items[idx]?.file;
        if (!file || signal.aborted) return;
        patchItem(id, idx, { status: 'uploading', pct: 100 });
        try {
          // The same helper the file path uses: a 429 here is the server PACING us, not a
          // failure. Without this, "too many requests" killed the row outright — which is what
          // made a large pick of zips mostly fail.
          const job = await whileServerBusy(
            () => importArchive(file, parentId, meta.domain, signal),
            signal,
            () => patchItem(id, idx, { status: 'queued', note: 'Waiting for the server…' }),
          );
          watching.set(job.id, idx);
          if (job.status === 'done' || job.status === 'failed') {
            watching.delete(job.id);
            report(idx, job);
          } else {
            patchItem(id, idx, { status: 'importing', note: undefined });
          }
        } catch (e) {
          patchItem(id, idx, {
            status: 'failed', error: e instanceof Error ? e.message : 'Unzip failed.',
          });
        }
      }));
    }
    sending = false;
  };

  const watch = async () => {
    let every = ARCHIVE_POLL_MIN_MS;
    // The deadline starts once SENDING is done, not when watching begins. Paced sending waits
    // out a busy server, and a big pick can take longer than the window on its own — zips sent
    // late would then never be polled and would report "still unpacking" whatever happened.
    let until = Infinity;
    while (!signal.aborted && Date.now() < until) {
      if (!sending && until === Infinity) until = Date.now() + ARCHIVE_WATCH_MS;
      if (!sending && watching.size === 0) return;
      await sleep(every, signal);
      if (signal.aborted) return;
      // Nobody is looking: the job belongs to the server once accepted and finishes on its own
      // (the sign-out path below relies on the same thing), so asking a hidden tab's worth of
      // questions every second buys nothing.
      if (typeof document !== 'undefined' && document.hidden) continue;
      if (watching.size === 0) continue;
      // In chunks: the route refuses more ids than ARCHIVE_POLL_CHUNK in one call, and the
      // catch below would swallow that refusal — every row would end up saying "still
      // unpacking", including ones the server had already failed.
      const ids = [...watching.keys()];
      let seen: ArchiveImportStatus[] = [];
      try {
        for (let i = 0; i < ids.length; i += ARCHIVE_POLL_CHUNK) {
          seen = seen.concat(await getArchiveImports(ids.slice(i, i + ARCHIVE_POLL_CHUNK)));
        }
      } catch {
        continue;   // a hiccup in the status call must not fail imports that are running fine
      }
      for (const st of seen) {
        if (st.status === 'pending' || st.status === 'importing') continue;
        const idx = watching.get(st.id);
        if (idx == null) continue;
        watching.delete(st.id);
        report(idx, st);
      }
      every = Math.min(Math.round(every * ARCHIVE_POLL_GROWTH), ARCHIVE_POLL_MAX_MS);
    }
  };

  await Promise.all([send(), watch()]);

  // Whatever is still running is the SERVER's job and will land. Say that rather than reporting
  // a break that did not happen — the same answer the old per-file loop gave on timeout.
  for (const [, idx] of watching) {
    patchItem(id, idx, {
      status: 'done', pct: 100,
      note: 'Still unpacking on our side — the files will appear in this folder shortly.',
    });
  }
  // Anything still being watched is running fine on the server and will land, so the folder stays.
  if (okCount === 0 && watching.size === 0 && createdFolderId != null) {
    // only_if_empty: even though the server just made this folder for us, ask it to remove the
    // folder ONLY while it is still empty — the server refuses (409) otherwise. Belt to the
    // create-only braces: nothing this run did can cascade-delete a populated folder.
    try { await deleteFolderNode(createdFolderId, { onlyIfEmpty: true }); } catch { /* best effort
      — a stray empty folder is better than an error on the way out */ }
  }
  return okCount;
}

function reviewNeeded(items: UploadItem[]): boolean {
  return items.some((it) => it.status === 'failed' || (it.skippedCount ?? 0) > 0 || (it.replacedCount ?? 0) > 0);
}

async function run(id: string, idxs: number[]) {
  const current = batch(id);
  if (!current) return;
  patchBatch(id, { running: true, needsReview: false });
  // Split the selection: regular files upload normally; archives are unzipped — both in one go.
  const regular: number[] = [];
  const archives: number[] = [];
  idxs.forEach((i) => (isArchiveFile(current.items[i]?.file.name ?? '') ? archives : regular).push(i));
  // allSettled, not all: `all` rejects the moment EITHER half throws, so a single failed file
  // ended the wait while a zip was still unpacking — the batch was declared finished, every
  // unfinished row marked failed, and the archive then turned green on its own minutes later.
  // Worse than the display: settling drops the "don't close this tab" guard while bytes are
  // still moving. A failure in one half is a failure of that half, not of the batch.
  // Captured ONCE, here. cancelAllUploads aborts this controller and immediately installs a
  // fresh one, so anything that re-reads the module variable later sees an un-aborted signal and
  // its guard silently does nothing. A batch is cancelled by the controller it began under.
  const signal = sessionCancel.signal;
  const [filesResult, archivesResult] = await Promise.allSettled([
    runFiles(id, regular, current.meta, signal),
    runArchives(id, archives, signal),
  ]);
  // One verdict per batch. A success toast stacked on a failure toast (seen live: "Uploaded 930
  // items." over "8 uploads failed.") reads as two answers to one action — so a batch with any
  // failure gets ONE line carrying both counts, and a clean batch gets one success line.
  let thrown: string | null = null;
  try {
    const failure = [filesResult, archivesResult].find((r) => r.status === 'rejected');
    if (failure && failure.status === 'rejected') {
      const reason: unknown = failure.reason;
      thrown = reason instanceof Error ? reason.message : 'Upload failed.';
      // Only rows that never reached a verdict are failed by the half that threw; anything
      // already done or already failed keeps what it earned.
      patchBatch(id, (b) => ({
        items: b.items.map((it) => (it.status === 'done' || it.status === 'failed'
          ? it
          : { ...it, status: 'failed', error: it.error ?? 'Upload failed.' })),
      }));
    }
  } finally {
    const done = batch(id);
    if (done) {
      const okTotal =
        (filesResult.status === 'fulfilled' ? filesResult.value : 0)
        + (archivesResult.status === 'fulfilled' ? archivesResult.value : 0);
      const failed = done.items.filter((it) => it.status === 'failed').length;
      // Skipped files are part of the outcome, not a silent detail: "Uploaded 930 items" after a
      // re-upload that stored nothing reads as 930 new documents (audit 2026-09-22).
      const skipped = done.items.filter((it) => it.outcome === 'skipped').length;
      const extra = skipped > 0 ? ` · ${skipped.toLocaleString()} skipped` : '';
      if (failed > 0) {
        toast.error(
          `${okTotal.toLocaleString()} uploaded · ${failed.toLocaleString()} failed${extra}`
          + (thrown ? ` — ${thrown}` : '')
          + '. Open the uploads panel to retry.',
        );
      } else if (okTotal > 0 || skipped > 0) {
        toast.success(`Uploaded ${okTotal.toLocaleString()} item${okTotal === 1 ? '' : 's'}${extra}.`);
      }
      patchBatch(id, { running: false, needsReview: reviewNeeded(done.items) });
      done.onSettled?.();
    }
  }
}

/** Hand a selection to the manager. Returns at once with the batch id; the upload runs on. */
export function startBatch(input: {
  files: File[];
  meta: BulkUploadMeta;
  destination: UploadDestination;
  label?: string;
  /** Called once when the batch has fully resolved — the caller refetches the library here. */
  onSettled?: () => void;
}): string {
  seq += 1;
  const id = `upload-${Date.now()}-${seq}`;
  const items: UploadItem[] = input.files.map((file) => ({ file, status: 'queued', pct: 0 }));
  setBatches((bs) => [...bs, {
    id,
    label: input.label ?? input.meta.doc_type,
    items,
    running: true,
    needsReview: false,
    meta: input.meta,
    destination: input.destination,
    onSettled: input.onSettled,
  }]);
  void run(id, items.map((_, i) => i));
  return id;
}

/** Re-run ONLY the failed rows of a settled batch. Rows that landed are live documents — a
 *  re-run of those would upload them again and mint duplicate numbers, so they never go back. */
export function retryFailed(id: string): void {
  const current = batch(id);
  if (!current || current.running) return;
  const idxs = current.items.map((it, i) => (it.status === 'failed' ? i : -1)).filter((i) => i >= 0);
  if (idxs.length === 0) return;
  idxs.forEach((i) => patchItem(id, i, { status: 'queued', pct: 0, error: undefined }));
  void run(id, idxs);
}

export function dismissBatch(id: string): void {
  const current = batch(id);
  if (!current || current.running) return;
  setBatches((bs) => bs.filter((b) => b.id !== id));
}

/**
 * End the session's uploads: stop what is moving and forget what was queued.
 *
 * Called on sign-out. Without it the manager outlives the person who started it — it is module
 * state with no owner, and signing out is a page change, not a reload. Their filenames stayed
 * on the login screen with a Retry button beside them, and a transfer already under way kept
 * going; whatever finished afterwards was filed under whoever signed in next, because the
 * finishing step carries the cookie of the moment, not of the person who began the upload.
 */
export function cancelAllUploads(): void {
  sessionCancel.abort();
  sessionCancel = new AbortController();
  snapshot = { batches: [] };
  emit();
}

export function resetUploadManagerForTests(): void {
  sessionCancel = new AbortController();
  snapshot = { batches: [] };
  // Subscribers too: a test helper that waits for a batch adds one each time it is called, and
  // leaving them attached means every later test notifies a growing crowd of dead listeners.
  listeners.clear();
  emit();
}
